import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import classNames from 'classnames';

import PageContainer from '../../components/Containers/PageContainer';

import { useAuth } from '../../../contexts/AuthContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagContext';
import InsightsFilterBar from '../../components/InsightsFilterBar';
import InsightsMetricCard from '../../components/InsightsMetricCard';
import PractitionerList from './PractitionerList';
import {
  InsightsFilterState,
  customRangeProblem,
  displayedMetrics,
  filterClientsByTags,
  formatRangeCaption,
  loadInsightsFilters,
  saveInsightsFilters,
  filtersBlockedByFailedLists,
  pruneFilters,
  shouldSkipMetricsQuery,
  toClientsDateRange,
  toQueryInput,
} from './insightsFilters';

import {
  Feature,
  useMeProviderProgramTagsQuery,
  usePractitionerAssignmentsQuery,
  useTeamPerformanceInsightsMetricsQuery,
  useProviderServicesQuery,
} from '../../../generated/graphql';

import { isProviderUserOwnerOrAdminAuthorized } from '../../../lib/auth';

const SESSIONS_TOOLTIP =
  'Bookings completed in this period, by booking date. Cancelled Calendly bookings are still counted.';
const CLIENTS_TOOLTIP = 'Clients added in this period.';

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const InsightsPage = () => {
  const { authedProviderUser, showUpgradeBanner } = useAuth();
  const {
    enabledFeatures,
    loading: featuresLoading,
    error: featuresError,
    isFeatureEnabled,
  } = useFeatureFlags();
  const featuresLoaded = enabledFeatures !== undefined;
  const canAccessInsights =
    featuresLoaded &&
    isFeatureEnabled(Feature.TeamPerformance) &&
    isProviderUserOwnerOrAdminAuthorized(authedProviderUser);

  const userId = authedProviderUser.id;
  const providerId = authedProviderUser.provider.id;
  const [filters, setFilters] = useState<InsightsFilterState>(() =>
    loadInsightsFilters(userId, providerId, null, null),
  );
  // Provider whose persisted filters have been pruned against its current tags and services.
  const [prunedFor, setPrunedFor] = useState<string | null>(null);

  const {
    data: tagsData,
    error: tagsError,
    refetch: refetchTags,
  } = useMeProviderProgramTagsQuery({
    skip: !canAccessInsights,
  });
  const {
    data: servicesData,
    error: servicesError,
    refetch: refetchServices,
  } = useProviderServicesQuery({
    skip: !canAccessInsights,
  });
  const tags = useMemo(
    () => tagsData?.meProvider?.provider?.programTags ?? [],
    [tagsData],
  );
  const services = useMemo(
    () =>
      (servicesData?.providerServices ?? []).map((service) => ({
        id: service.id,
        name: service.name,
      })),
    [servicesData],
  );

  const listsSettled =
    Boolean(tagsData || tagsError) && Boolean(servicesData || servicesError);

  const knownTagIds = useMemo(
    () => (tagsData ? new Set(tags.map((tag) => tag.id)) : null),
    [tagsData, tags],
  );
  const knownServiceIds = useMemo(
    () =>
      servicesData ? new Set(services.map((service) => service.id)) : null,
    [servicesData, services],
  );

  // Persisted ids missing from a loaded list are dropped (the server rejects unknown ids). Derived at
  // render time, not in an effect, so a list that loads late (e.g. on Retry) never queries with a stale id.
  // A list that failed keeps its ids and blocks the query below.
  const activeFilters = useMemo(
    () => pruneFilters(filters, knownTagIds, knownServiceIds),
    [filters, knownTagIds, knownServiceIds],
  );

  // Write the pruned ids back (state and storage) so they stop being shown and don't return on reload.
  useEffect(() => {
    if (!listsSettled) {
      return;
    }
    const pruned = pruneFilters(filters, knownTagIds, knownServiceIds);
    if (JSON.stringify(pruned) !== JSON.stringify(filters)) {
      setFilters(pruned);
      saveInsightsFilters(userId, providerId, pruned);
    }
    setPrunedFor(providerId);
  }, [listsSettled, knownTagIds, knownServiceIds, providerId, filters, userId]);

  const blockedLists = filtersBlockedByFailedLists(activeFilters, {
    tagsFailed: !tagsData && !!tagsError,
    servicesFailed: !servicesData && !!servicesError,
  });

  const retryBlockedLists = () => {
    if (blockedLists.includes('tags')) {
      refetchTags();
    }
    if (blockedLists.includes('services')) {
      refetchServices();
    }
  };

  const handleFiltersChange = (next: InsightsFilterState) => {
    setFilters(next);
    saveInsightsFilters(userId, providerId, next);
  };

  const rangeProblem = customRangeProblem(activeFilters);

  const {
    data: metricsData,
    previousData: previousMetricsData,
    loading: metricsLoading,
    error: metricsError,
  } = useTeamPerformanceInsightsMetricsQuery({
    variables: {
      providerId,
      input: toQueryInput(activeFilters, browserTimezone()),
    },
    skip: shouldSkipMetricsQuery({
      canAccess: canAccessInsights,
      filters: activeFilters,
      listsSettled: prunedFor === providerId,
      blockedByFailedLists: blockedLists.length > 0,
    }),
    fetchPolicy: 'cache-and-network',
  });

  // No numbers while the range is invalid or the filter can't be applied: the last ones belong to a different filter.
  const metrics =
    rangeProblem || blockedLists.length > 0
      ? undefined
      : displayedMetrics(
          metricsData?.teamPerformanceInsightsMetrics,
          previousMetricsData?.teamPerformanceInsightsMetrics,
          !!metricsError,
        );

  // The client lists need the range the server counted. If the metrics request fails, reuse the last one for
  // the same period; with none known the lists still show, just without stats.
  const countedRange = useMemo(
    () =>
      metrics ? toClientsDateRange(metrics.rangeStart, metrics.rangeEnd) : null,
    [metrics],
  );
  const rangeKey = `${activeFilters.preset}|${activeFilters.startDate}|${activeFilters.endDate}`;
  const lastRangeRef = useRef<{
    key: string;
    range: NonNullable<typeof countedRange>;
  } | null>(null);
  // Remember only the current request's own range: while a new period loads, `metrics` still holds the previous one.
  const freshMetrics = metricsData?.teamPerformanceInsightsMetrics;
  useEffect(() => {
    if (freshMetrics) {
      lastRangeRef.current = {
        key: rangeKey,
        range: toClientsDateRange(
          freshMetrics.rangeStart,
          freshMetrics.rangeEnd,
        ),
      };
    }
  }, [freshMetrics, rangeKey]);
  const clientsRange =
    countedRange ??
    (lastRangeRef.current?.key === rangeKey
      ? lastRangeRef.current.range
      : null);
  const showClientLists =
    !rangeProblem &&
    blockedLists.length === 0 &&
    (clientsRange !== null || !!metricsError);

  const { data: practitionersData, loading: practitionersLoading } =
    usePractitionerAssignmentsQuery({
      variables: { providerId },
      skip: !canAccessInsights,
    });

  // Tag filters narrow each practitioner's clients too; with a tag filter on, practitioners left with none are hidden.
  const tagFilterActive =
    activeFilters.includeTagIds.length > 0 ||
    activeFilters.excludeTagIds.length > 0;
  const practitioners = useMemo(
    () =>
      (practitionersData?.practitionerAssignments ?? [])
        .map((assignment) => ({
          ...assignment,
          assignedClients: filterClientsByTags(
            assignment.assignedClients,
            activeFilters.includeTagIds,
            activeFilters.excludeTagIds,
          ),
        }))
        .filter(
          (assignment) =>
            !tagFilterActive || assignment.assignedClients.length > 0,
        ),
    [
      practitionersData,
      activeFilters.includeTagIds,
      activeFilters.excludeTagIds,
      tagFilterActive,
    ],
  );

  if (featuresLoading && !featuresLoaded) {
    return null;
  }

  // Failed flag fetch or missing access → leave Insights rather than a blank page.
  if (featuresError || !canAccessInsights) {
    return <Navigate to="/home" replace />;
  }

  return (
    <PageContainer
      containerClassName={classNames(
        showUpgradeBanner
          ? 'h-[calc(100vh-var(--top-nav-height)-var(--upgrade-banner-height))] overflow-y-hidden'
          : 'h-[calc(100vh-var(--top-nav-height))] overflow-y-hidden',
      )}
    >
      <div className="mb-4 flex flex-row items-center">
        <div className="font-serif text-subtitle-small text-green-150 md:text-subtitle">
          Insights
        </div>
      </div>

      <hr className="mb-6 border-neutral-75" />

      <section className="flex h-[calc(100vh-232px)] w-full flex-col items-start overflow-y-auto px-2 pb-10 pt-2">
        <InsightsFilterBar
          value={activeFilters}
          onChange={handleFiltersChange}
          tags={tags}
          services={services}
        />

        {metricsError && (
          <div className="mb-4 font-sans text-caption text-red-100">
            {metricsError.message}
          </div>
        )}
        {blockedLists.length > 0 && (
          <div className="mb-4 flex items-center gap-x-3 font-sans text-caption text-red-100">
            Couldn&apos;t load your {blockedLists.join(' and ')}, so the saved
            filter can&apos;t be applied.
            <button
              type="button"
              className="font-medium text-secondary-150 underline"
              onClick={retryBlockedLists}
            >
              Retry
            </button>
          </div>
        )}
        {rangeProblem === 'incomplete' && (
          <div className="mb-4 font-sans text-caption text-neutral-110">
            Pick a start and end date to see the numbers.
          </div>
        )}
        {rangeProblem === 'reversed' && (
          <div className="mb-4 font-sans text-caption text-red-100">
            The start date must be on or before the end date.
          </div>
        )}

        <div className="grid w-full max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
          <InsightsMetricCard
            label="Sessions booked"
            tooltip={SESSIONS_TOOLTIP}
            value={metrics?.sessionsBooked.value}
            loading={metricsLoading}
            error={!!metricsError && !metrics}
          />
          <InsightsMetricCard
            label="New clients"
            tooltip={CLIENTS_TOOLTIP}
            value={metrics?.newClients.value}
            loading={metricsLoading}
            error={!!metricsError && !metrics}
          />
        </div>

        {metrics && !rangeProblem && (
          <div className="mt-3 font-sans text-caption text-neutral-110">
            {formatRangeCaption(
              metrics.rangeStart,
              metrics.rangeEnd,
              metrics.timezone,
            )}
          </div>
        )}

        {showClientLists && (
          <div className="mt-10 flex w-full flex-col">
            <PractitionerList
              practitioners={practitioners}
              loading={practitionersLoading}
              dateRange={clientsRange}
            />
          </div>
        )}
      </section>
    </PageContainer>
  );
};

export default InsightsPage;
